import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save, Newspaper, Sparkles, Link as LinkIcon, FileUp, Loader2 } from 'lucide-react';
import { Section, Field, Modal, ImageUpload } from './_shared';
import { fileToDataUrl } from '../../../lib/image';

const empty = { title: '', summary: '', body: '', cover_base64: '', link: '', date: '', order: 0 };

export default function NewsPanel({ cx }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiUrl, setAiUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const pdfInputRef = useRef(null);

  const load = async () => { setLoading(true); try { const r = await cx.get('/news'); setItems(r.data || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing }; delete payload.id; delete payload.created_at;
      if (editing.id) await cx.put(`/news/${editing.id}`, payload);
      else await cx.post('/news', payload);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try { await cx.delete(`/news/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const applyAiDraft = (draft) => {
    setEditing((cur) => ({
      ...(cur || empty),
      title: draft.title || cur?.title || '',
      summary: draft.summary || cur?.summary || '',
      body: draft.body || cur?.body || '',
      date: draft.date || cur?.date || '',
      link: draft.link || cur?.link || '',
      cover_base64: draft.cover_base64 || cur?.cover_base64 || '',
    }));
  };

  const generateFromUrl = async () => {
    const url = (aiUrl || '').trim();
    if (!url) { toast.error('Paste a news URL first'); return; }
    setGenerating(true);
    try {
      const { data } = await cx.post('/news/from-source', { url });
      applyAiDraft(data);
      toast.success('Draft generated — review and edit before saving.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI draft failed');
    } finally { setGenerating(false); }
  };

  const generateFromPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please pick a PDF file'); return;
    }
    if (file.size > 15 * 1024 * 1024) { toast.error('PDF too large (max 15 MB)'); return; }
    setGenerating(true);
    try {
      const b64 = await fileToDataUrl(file);
      const { data } = await cx.post('/news/from-source', { pdf_base64: b64 });
      applyAiDraft(data);
      toast.success('Draft generated from PDF — review and edit before saving.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI draft failed');
    } finally { setGenerating(false); }
  };

  return (
    <Section title="News Room" action={<button data-testid="admin-news-add" onClick={() => { setEditing({ ...empty }); setAiUrl(''); }} className="btn-primary text-sm"><Plus size={14} /> New article</button>}>
      {loading ? <p className="text-sec text-sm font-mono">Loading…</p> : items.length === 0 ? <p className="text-muted text-sm">No articles yet.</p> : (
        <div className="space-y-3">
          {items.map((n) => (
            <div key={n.id} data-testid={`admin-news-row-${n.id}`} className="border border-subtle surface rounded-md p-4 flex gap-4 items-start">
              <div className="w-20 h-20 rounded-md overflow-hidden surface-2 shrink-0 flex items-center justify-center">
                {n.cover_base64 ? <img src={n.cover_base64} className="photo-fit" alt="" /> : <Newspaper size={20} className="text-muted" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-prim">{n.title}</p>
                <p className="text-xs text-muted font-mono mt-1">{n.date}</p>
                {n.summary && <p className="text-sec text-sm mt-1 line-clamp-2">{n.summary}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => { setEditing(n); setAiUrl(''); }} className="text-xs text-brand-teal-light hover:underline flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(n.id)} className="text-xs text-brand-maroon-light hover:underline flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Edit article' : 'New article'}>
          <div className="space-y-4">
            {/* AI draft helper */}
            <div data-testid="admin-news-ai-card" className="border border-brand-teal/30 bg-brand-teal/5 rounded-md p-4 space-y-3">
              <p className="font-display text-prim text-sm flex items-center gap-2"><Sparkles size={14} className="text-brand-teal-light" /> Draft from a source</p>
              <p className="text-xs text-muted leading-relaxed">Paste a news URL or upload a PDF press clipping. Claude Sonnet 4.6 will produce a title, summary and 3–5 paragraph body in MBK editorial tone. Review before saving.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 flex gap-2">
                  <div className="relative flex-1">
                    <LinkIcon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    <input
                      data-testid="admin-news-ai-url"
                      className="lux-input pl-9 w-full"
                      placeholder="https://example.com/article"
                      value={aiUrl}
                      onChange={(e) => setAiUrl(e.target.value)}
                      disabled={generating}
                    />
                  </div>
                  <button
                    data-testid="admin-news-ai-url-go"
                    type="button"
                    onClick={generateFromUrl}
                    disabled={generating || !aiUrl.trim()}
                    className="btn-primary text-xs whitespace-nowrap disabled:opacity-60"
                  >
                    {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {generating ? 'Drafting…' : 'Draft from URL'}
                  </button>
                </div>
                <button
                  data-testid="admin-news-ai-pdf"
                  type="button"
                  onClick={() => pdfInputRef.current?.click()}
                  disabled={generating}
                  className="btn-secondary text-xs whitespace-nowrap disabled:opacity-60"
                >
                  {generating ? <Loader2 size={12} className="animate-spin" /> : <FileUp size={12} />}
                  {generating ? 'Drafting…' : 'Draft from PDF'}
                </button>
                <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={generateFromPdf} />
              </div>
            </div>

            <ImageUpload value={editing.cover_base64} onChange={(v) => setEditing({ ...editing, cover_base64: v })} label="Cover image" />
            <Field label="Title"><input className="lux-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="Date (YYYY-MM-DD)"><input className="lux-input" placeholder="2025-12-01" value={editing.date || ''} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></Field>
            <Field label="Summary"><textarea rows={2} className="lux-input resize-none" value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} /></Field>
            <Field label="Body (optional)"><textarea rows={8} className="lux-input resize-none" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} /></Field>
            <Field label="External link (optional)"><input className="lux-input" value={editing.link} onChange={(e) => setEditing({ ...editing, link: e.target.value })} /></Field>
            <Field label="Order"><input type="number" className="lux-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0) })} /></Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={save} data-testid="admin-news-save" className="btn-primary text-sm"><Save size={14} /> Save</button>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  );
}
